import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd as SignupIcon,
  Person as PersonIcon,
  VerifiedUser as VerifyIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, signup, confirmSignup, isAuthenticated, loading } = useAuth();
  const { isConfigured } = useConfig();
  
  const [activeTab, setActiveTab] = useState(0); // 0 = Login, 1 = Signup, 2 = Verify
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    verificationCode: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(''); // Store email for verification

  useEffect(() => {
    if (!isConfigured) {
      navigate('/config');
      return;
    }
    
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, isConfigured, navigate]);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setSuccess('');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      verificationCode: '',
    });
  };

  const validateForm = () => {
    if (activeTab === 2) { // Verification
      if (!formData.verificationCode) {
        setError('Verification code is required');
        return false;
      }
      return true;
    }

    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      return false;
    }

    if (activeTab === 1) { // Signup validation
      if (!formData.name) {
        setError('Name is required for signup');
        return false;
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      if (activeTab === 0) {
        // Login
        const result = await login(formData.email, formData.password);
        if (result.success) {
          navigate('/');
        } else {
          setError(result.error || 'Login failed');
        }
      } else if (activeTab === 1) {
        // Signup
        const result = await signup(formData.email, formData.password, formData.name);
        if (result.success) {
          if (result.needsConfirmation) {
            setPendingEmail(formData.email);
            setSuccess('Account created! Please check your email for the verification code.');
            setActiveTab(2); // Switch to verification tab
            setFormData(prev => ({
              ...prev,
              verificationCode: '',
            }));
          } else {
            setSuccess('Account created and verified! You can now login.');
            setActiveTab(0); // Switch to login tab
            setFormData({
              email: formData.email,
              password: '',
              confirmPassword: '',
              name: '',
              verificationCode: '',
            });
          }
        } else {
          setError(result.error || 'Signup failed');
        }
      } else if (activeTab === 2) {
        // Email Verification
        const result = await confirmSignup(pendingEmail, formData.verificationCode);
        if (result.success) {
          setSuccess('Email verified successfully! You can now login.');
          setActiveTab(0); // Switch to login tab
          setFormData({
            email: pendingEmail,
            password: '',
            confirmPassword: '',
            name: '',
            verificationCode: '',
          });
          setPendingEmail('');
        } else {
          setError(result.error || 'Verification failed');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center">
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <PersonIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            AI Recruitment System
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {activeTab === 0 && 'Sign in to your account'}
            {activeTab === 1 && 'Create a new account'}
            {activeTab === 2 && 'Enter the verification code sent to your email'}
          </Typography>
        </Box>

        {/* Tabs for Login/Signup/Verify */}
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          centered 
          sx={{ mb: 3 }}
        >
          <Tab 
            icon={<LoginIcon />} 
            label="Login" 
            iconPosition="start"
            disabled={activeTab === 2} // Disable when in verification mode
          />
          <Tab 
            icon={<SignupIcon />} 
            label="Sign Up" 
            iconPosition="start"
            disabled={activeTab === 2} // Disable when in verification mode
          />
          {activeTab === 2 && (
            <Tab 
              icon={<VerifyIcon />} 
              label="Verify Email" 
              iconPosition="start"
            />
          )}
        </Tabs>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Verification Code field */}
          {activeTab === 2 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We sent a verification code to: <strong>{pendingEmail}</strong>
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={formData.verificationCode}
                onChange={handleInputChange('verificationCode')}
                margin="normal"
                required
                placeholder="Enter 6-digit code"
                inputProps={{ maxLength: 6 }}
              />
            </>
          )}

          {/* Name field for signup */}
          {activeTab === 1 && (
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={handleInputChange('name')}
              margin="normal"
              required
              autoComplete="name"
            />
          )}

          {/* Email field for login and signup */}
          {activeTab !== 2 && (
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              margin="normal"
              required
              autoComplete="email"
            />
          )}

          {/* Password field for login and signup */}
          {activeTab !== 2 && (
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange('password')}
              margin="normal"
              required
              autoComplete={activeTab === 0 ? 'current-password' : 'new-password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {/* Confirm Password field for signup */}
          {activeTab === 1 && (
            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              margin="normal"
              required
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            startIcon={
              activeTab === 0 ? <LoginIcon /> : 
              activeTab === 1 ? <SignupIcon /> : 
              <VerifyIcon />
            }
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {isLoading 
              ? (activeTab === 0 ? 'Signing In...' : 
                 activeTab === 1 ? 'Creating Account...' : 
                 'Verifying...') 
              : (activeTab === 0 ? 'Sign In' : 
                 activeTab === 1 ? 'Create Account' : 
                 'Verify Email')
            }
          </Button>

          {activeTab === 1 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
              By creating an account, you agree to our terms of service and privacy policy.
            </Typography>
          )}

          {activeTab === 2 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
              Didn't receive the code? Check your spam folder or try signing up again.
            </Typography>
          )}
        </form>
      </Paper>
    </Container>
  );
};

export default LoginPage;
